import type { Cart } from '@/types/booking';

import { usePaymentSessionQuery } from '../queries/bnpl/usePaymentSessionQuery';
import type { CashAppPaymentMethodAvailability } from '../queries/bnpl/usePaymentSessionQuery/types';

/**
 * Whether Cash App is available as a payment method. Eligibility is decided by
 * the backend (`/v1/payment/session/:cartId`), mirroring `useBnplEligibility` —
 * the `cash_app` entry's `eligible` flag already reflects the server-side
 * master flag (`flag_master_off`) and currency checks (`non_usd_currency`).
 *
 * Note: the caller is still responsible for the web-only guard — native
 * (iOS/Android) has no Cash App flow regardless of backend eligibility.
 */
export const useCashAppEligibility = (
  cart: Cart | null | undefined,
): boolean => {
  const { data: paymentSession } = usePaymentSessionQuery({
    cartId: cart?.id,
  });

  const cashAppEntry = paymentSession?.availablePaymentMethods.find(
    (m): m is CashAppPaymentMethodAvailability => m.type === 'cash_app',
  );

  return cashAppEntry?.eligible ?? false;
};
